import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth';
import { generateTraceId } from '@/lib/trace';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------- Request interceptor: bearer + trace ID ---------- //
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Trace-Id'] = generateTraceId();
  return config;
});

// ---------- Response interceptor: 401 silent refresh ---------- //
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const res = await axios.post<{ access_token: string; refresh_token: string }>(
      `${baseURL}/auth/refresh`,
      { refresh_token: refresh },
      { headers: { 'Content-Type': 'application/json' } },
    );
    useAuthStore.getState().setTokens(res.data.access_token, res.data.refresh_token);
    return res.data.access_token;
  } catch {
    useAuthStore.getState().logout();
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as RetryableConfig | undefined;

    // IP-allowlist 403 → bounce to access-denied page (Caddy-served)
    if (status === 403) {
      const data = error.response?.data as { error?: { code?: string } } | undefined;
      if (data?.error?.code === 'IP_NOT_ALLOWED' && typeof window !== 'undefined') {
        window.location.href = '/access-denied';
        return Promise.reject(error);
      }
    }

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      refreshPromise ??= performRefresh().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
    }

    return Promise.reject(error);
  },
);
